import React, { useEffect, useMemo, useState } from "react";
import useAuth from "../../hooks/useAuth";
import apiClient from "../../store/services/apiClient";
import styles from "./AutoPromoTab.module.css";
import { useDispatch } from "react-redux";
import { getUsersByID } from "../../store/slices/user";

export default function AutoPromoTab() {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const [userDetail, setUserDetail] = useState(null);
  const [available, setAvailable] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(null);
  const [loading, setLoading] = useState(false);

  const disabledSelect =
    userDetail?.PromotionStatus === "ACTIVE" ||
    !!userDetail?.withdrawConditionType;

  const normalizePromoText = (value) => {
    const text = String(value ?? "").trim();
    return text && text.toUpperCase() !== "NULL" ? text : "";
  };

  const normalizePromoId = (value) => {
    const text = String(value ?? "").trim();
    return text && text.toUpperCase() !== "NULL" ? value : null;
  };

  const makeImageUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const base = process.env.REACT_APP_API_BASE_URL || "";
    const sep = path.startsWith("/") ? "" : "/";
    return `${base}${sep}${path}`;
  };

  const promoCards = useMemo(() => {
    return (available || []).map((p) => ({
      id: p?.PromotionID,
      detail: p?.Detail,
      image: makeImageUrl(p?.imageUrl),
      raw: p,
    }));
  }, [available]);

  useEffect(() => {
    if (!user) return;
    setSelectedId(normalizePromoId(user?.PromotionID));
    setSelectedDetail(normalizePromoText(user?.PromotionDetail));
  }, [user]);

  useEffect(() => {
    if (!userDetail) return;
    setSelectedId(normalizePromoId(userDetail?.PromotionID));
    setSelectedDetail(normalizePromoText(userDetail?.PromotionDetail));
  }, [userDetail]);

  // Fetch immediately on mount/user change (no button needed)
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const fetchAll = async () => {
      try {
        const [{ data: userRes }, { data: promoRes }] = await Promise.all([
          apiClient.get(`/usersystem/${user.id}`, {
            headers: {
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Pragma: "no-cache",
              Expires: "0",
            },
          }),
          apiClient.get(`/usersystem/getavailablepromotion/${user.id}`),
        ]);
        const u = Array.isArray(userRes?.data)
          ? userRes.data[0]
          : userRes?.data;
        setUserDetail(u || null);
        setAvailable(promoRes?.data || promoRes || []);
      } catch (e) {
        console.error("load data error", e?.message || e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user]);

  useEffect(() => {
    if (!promoCards?.length) return;
    const idToFind = normalizePromoId(userDetail?.PromotionID) ?? selectedId;
    const idx = promoCards.findIndex((p) => p.id === idToFind);
    setHighlightIdx(idx >= 0 ? idx : null);
  }, [promoCards, userDetail, selectedId]);

  const handleSelectCard = (index) => {
    if (disabledSelect) return;
    const item = promoCards[index];
    if (!item) return;
    setPending({ index, item });
    setConfirmOpen(true);
  };

  const toast = (msg) => {
    try {
      if (window?.dispatchEvent) {
        const ev = new CustomEvent("app:toast", { detail: { message: msg } });
        window.dispatchEvent(ev);
      }
    } catch (_) {}
    console.log(msg);
  };

  const applyPromotion = async (promotionId, detail) => {
    try {
      const normalizedId = normalizePromoId(promotionId);
      const normalizedDetail = normalizePromoText(detail);
      const makeBody = (forceNull = false) => {
        const body = new URLSearchParams();
        body.set(
          "PromotionID",
          normalizedId == null
            ? forceNull
              ? "NULL"
              : ""
            : String(normalizedId),
        );
        body.set(
          "PromotionDetail",
          normalizedDetail || (normalizedId == null && forceNull ? "NULL" : ""),
        );
        return body;
      };

      try {
        await apiClient.put(
          `/usersystem/promotion/${user?.id}`,
          makeBody(false).toString(),
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
          },
        );
      } catch (firstErr) {
        if (normalizedId != null) throw firstErr;
        await apiClient.put(
          `/usersystem/promotion/${user?.id}`,
          makeBody(true).toString(),
          {
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
          },
        );
      }
      // Refresh user details & available list
      const [{ data: userRes }, { data: promoRes }] = await Promise.all([
        apiClient.get(`/usersystem/${user.id}`),
        apiClient.get(`/usersystem/getavailablepromotion/${user.id}`),
      ]);
      const u = Array.isArray(userRes?.data) ? userRes.data[0] : userRes?.data;
      setUserDetail(u || null);
      setAvailable(promoRes?.data || promoRes || []);
      if (user?.id) dispatch(getUsersByID(user.id));
      // Also refresh global user state for App.js
      if (user?.id) dispatch(getUsersByID(user.id));
      if (window?.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent("app:userUpdate", { detail: { userId: user?.id } }),
        );
      }
      toast(
        normalizedId != null
          ? "เลือกโปรโมชั่นเรียบร้อย"
          : "เปลี่ยนเป็นไม่รับโปรโมชั่นแล้ว",
      );
    } catch (e) {
      console.error("applyPromotion error", e?.message || e);
      toast("ไม่สามารถอัปเดตโปรโมชั่นได้");
    }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const { item, index } = pending;
    setSelectedId(item.id);
    setSelectedDetail(item.detail);
    setHighlightIdx(index);
    setConfirmOpen(false);
    setPending(null);
    await applyPromotion(item.id, item.detail);
  };

  const handleNonPro = async () => {
    setSelectedId(null);
    setSelectedDetail("");
    setHighlightIdx(null);
    await applyPromotion(null, "");
  };

  return (
    <div className={styles.wrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <svg
            className={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h4 className={styles.title}>📋 การจัดการโปรโมชั่น</h4>
        <p className={styles.subtitle}>จัดการและดูข้อมูลโปรโมชั่นของท่าน</p>
      </div>

      {/* Status Card */}
      <div className={styles.statusCard}>
        <div className={styles.statusIcon}>
          {selectedDetail ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        <div className={styles.statusContent}>
          <div className={styles.statusLabel}>โปรโมชั่นที่เลือกไว้</div>
          <div
            className={`${styles.statusValue} ${
              selectedDetail ? styles.active : styles.inactive
            }`}
          >
            {selectedDetail || "ไม่ได้รับโปรโมชั่น"}
          </div>
        </div>
      </div>

      {/* Warning Message */}
      {disabledSelect && (
        <div className={styles.warningCard}>
          <div className={styles.warningIcon}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className={styles.warningText}>
            <h4>ไม่สามารถเปลี่ยนโปรโมชั่นได้</h4>
            <p>
              ท่านอยู่ระหว่างใช้โปรโมชั่น หรือมียอดที่ต้องเล่น
              กรุณาเคลียร์ยอดเล่นให้ครบก่อน
            </p>
          </div>
        </div>
      )}

      {/* Promotions Grid */}
      <div className={styles.promotionsSection}>
        <div className={styles.sectionHeader}>
          <h3>โปรโมชั่นที่สามารถเลือกได้</h3>
          {!disabledSelect && (
            <button onClick={handleNonPro} className={styles.noPromoButton}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              ไม่ใช้โปรโมชั่น
            </button>
          )}
        </div>

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>กำลังโหลดข้อมูลโปรโมชั่น...</p>
          </div>
        ) : (
          <div className={styles.promotionsGrid}>
            {promoCards && promoCards.length > 0 ? (
              promoCards.map((it, idx) => (
                <div
                  key={`${it.id}-${idx}`}
                  className={`${styles.promoCard} ${
                    highlightIdx === idx ? styles.selectedCard : ""
                  } ${disabledSelect ? styles.disabledCard : ""}`}
                  onClick={() => handleSelectCard(idx)}
                >
                  <div className={styles.cardImageWrapper}>
                    {it.image ? (
                      <img
                        src={it.image}
                        alt={it.detail}
                        className={styles.cardImage}
                      />
                    ) : (
                      <div className={styles.placeholderImage}>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                    {highlightIdx === idx && (
                      <div className={styles.selectedBadge}>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className={styles.cardContent}>
                    <h4 className={styles.cardTitle}>{it.detail}</h4>
                    <p className={styles.cardDescription}>
                      {disabledSelect
                        ? "ไม่สามารถเลือกได้ในขณะนี้"
                        : "คลิกเพื่อเลือกโปรโมชั่นนี้"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 12H16M12 8V16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3>ไม่มีโปรโมชั่น</h3>
                <p>ไม่มีโปรโมชั่นให้เลือกในขณะนี้</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {confirmOpen && pending && (
        <div
          className={styles.modalOverlay}
          onClick={() => setConfirmOpen(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>ยืนยันการเลือกโปรโมชั่น</h3>
            </div>

            {pending.item?.image && (
              <div className={styles.modalImageWrapper}>
                <img
                  src={pending.item.image}
                  alt={pending.item.detail}
                  className={styles.modalImage}
                />
              </div>
            )}

            <div className={styles.modalContent}>
              <p className={styles.modalQuestion}>
                ท่านต้องการเลือกโปรโมชั่นนี้หรือไม่?
              </p>
              <div className={styles.modalPromoName}>
                {pending.item?.detail}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                onClick={() => setConfirmOpen(false)}
                className={styles.cancelButton}
              >
                ยกเลิก
              </button>
              <button onClick={handleConfirm} className={styles.confirmButton}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                ยืนยันเลือก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
